import { Button, Card, CardBody, CardTitle, EnergyPicker } from '@fold-and-flow/ui'
import { useForm } from '@tanstack/react-form'
import { useNavigate } from '@tanstack/react-router'
import { AnimatePresence, motion } from 'framer-motion'
import { useState } from 'react'
import { z } from 'zod'
import { trpc } from '#lib/trpc'

type Step = 'energy' | 'time' | 'focus' | 'injuries' | 'intention'

const DURATION_OPTIONS = [20, 30, 45, 60] as const

const checkInFormSchema = z.object({
  energyLevel: z.number().int().min(1).max(5),
  availableMinutes: z.union([z.literal(20), z.literal(30), z.literal(45), z.literal(60)]),
  focusAreas: z.array(z.string()),
  activeInjuries: z.array(z.string()),
  intention: z.string(),
})

type CheckInFormValues = z.infer<typeof checkInFormSchema>

const defaultValues: CheckInFormValues = {
  energyLevel: 3,
  availableMinutes: 30,
  focusAreas: [],
  activeInjuries: [],
  intention: '',
}

const STEPS: Step[] = ['energy', 'time', 'focus', 'injuries', 'intention']

const FOCUS_OPTIONS = [
  'Hips',
  'Hamstrings',
  'Lower back',
  'Shoulders',
  'Core',
  'Chest',
  'Balance',
  'Twist',
  'Inversions',
  'Full body',
]

const slide = {
  initial: { opacity: 0, x: 24 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -24 },
  transition: { duration: 0.25, ease: 'easeInOut' as const },
}

export function CheckInPage() {
  const navigate = useNavigate()
  const [step, setStep] = useState<Step>('energy')

  const [error, setError] = useState<string | null>(null)

  const createCheckIn = trpc.checkIn.create.useMutation()
  const generateFlow = trpc.flow.generate.useMutation({
    onSuccess: async ({ flowId }) => {
      await navigate({ to: '/flow/$flowId', params: { flowId } })
    },
    onError: (err) => {
      // Surface a friendly message when no clips are in the library yet
      if (
        err.message.toLowerCase().includes('clip') ||
        err.message.toLowerCase().includes('eligible')
      ) {
        setError('No video clips are in the library yet. Ask your instructor to add some!')
      } else {
        setError('Something went wrong generating your flow. Please try again.')
      }
    },
  })

  const form = useForm({
    defaultValues,
    validators: {
      onSubmit: checkInFormSchema,
    },
    onSubmit: async ({ value }) => {
      const parsed = checkInFormSchema.parse(value)

      const { id: checkInId } = await createCheckIn.mutateAsync({
        energyLevel: parsed.energyLevel,
        availableMinutes: parsed.availableMinutes,
        focusAreas: parsed.focusAreas,
        activeInjuries: parsed.activeInjuries,
        intention: parsed.intention || null,
      })
      await generateFlow.mutateAsync({ checkInId })
    },
  })

  function advance() {
    const idx = STEPS.indexOf(step)
    const next = STEPS[idx + 1]
    if (next) setStep(next)
  }

  const isGenerating = createCheckIn.isPending || generateFlow.isPending

  return (
    <div className="max-w-md mx-auto">
      <div className="flex gap-1.5 mb-8">
        {STEPS.map((s, i) => (
          <div
            key={s}
            className={`h-1 flex-1 rounded-full transition-colors duration-300 ${
              STEPS.indexOf(step) >= i ? 'bg-terracotta-400' : 'bg-cream-200'
            }`}
          />
        ))}
      </div>

      <AnimatePresence mode="wait">
        {step === 'energy' && (
          <motion.div key="energy" {...slide}>
            <Card elevated>
              <CardBody>
                <CardTitle className="mb-6">How's your energy today?</CardTitle>
                <form.Field name="energyLevel">
                  {(field) => (
                    <EnergyPicker
                      value={field.state.value}
                      onChange={(v) => field.handleChange(v)}
                      className="mb-8"
                    />
                  )}
                </form.Field>
                <form.Subscribe selector={(s) => s.values.energyLevel}>
                  {(energy) => (
                    <Button size="lg" className="w-full" disabled={!energy} onClick={advance}>
                      Continue →
                    </Button>
                  )}
                </form.Subscribe>
              </CardBody>
            </Card>
          </motion.div>
        )}

        {step === 'time' && (
          <motion.div key="time" {...slide}>
            <Card elevated>
              <CardBody>
                <CardTitle className="mb-6">How much time do you have?</CardTitle>
                <form.Field name="availableMinutes">
                  {(field) => (
                    <div className="grid grid-cols-4 gap-2.5 mb-8">
                      {DURATION_OPTIONS.map((d) => (
                        <button
                          key={d}
                          type="button"
                          onClick={() => field.handleChange(d)}
                          className={`py-4 rounded-2xl text-center text-sm font-medium border transition-all ${
                            field.state.value === d
                              ? 'border-terracotta-400 bg-terracotta-400/5 text-terracotta-600'
                              : 'border-cream-200 bg-white text-warm-gray-700 hover:border-warm-gray-400'
                          }`}
                        >
                          {d}m
                        </button>
                      ))}
                    </div>
                  )}
                </form.Field>
                <Button size="lg" className="w-full" onClick={advance}>
                  Continue →
                </Button>
              </CardBody>
            </Card>
          </motion.div>
        )}

        {step === 'focus' && (
          <motion.div key="focus" {...slide}>
            <Card elevated>
              <CardBody>
                <CardTitle className="mb-1">Any area to focus on?</CardTitle>
                <p className="text-sm text-warm-gray-400 mb-6">
                  Optional — skip if you're open to anything
                </p>
                <form.Field name="focusAreas">
                  {(field) => (
                    <div className="flex flex-wrap gap-2 mb-8">
                      {FOCUS_OPTIONS.map((opt) => {
                        const selected = field.state.value.includes(opt)
                        return (
                          <button
                            key={opt}
                            type="button"
                            onClick={() =>
                              field.handleChange(
                                selected
                                  ? field.state.value.filter((v) => v !== opt)
                                  : [...field.state.value, opt]
                              )
                            }
                            className={`px-3.5 py-2 rounded-full text-sm border transition-all ${
                              selected
                                ? 'border-terracotta-400 bg-terracotta-400/5 text-terracotta-600 font-medium'
                                : 'border-cream-200 bg-white text-warm-gray-600 hover:border-warm-gray-400'
                            }`}
                          >
                            {opt}
                          </button>
                        )
                      })}
                    </div>
                  )}
                </form.Field>
                <Button size="lg" className="w-full" onClick={advance}>
                  Continue →
                </Button>
              </CardBody>
            </Card>
          </motion.div>
        )}

        {step === 'injuries' && (
          <motion.div key="injuries" {...slide}>
            <Card elevated>
              <CardBody>
                <CardTitle className="mb-1">Any discomfort today?</CardTitle>
                <p className="text-sm text-warm-gray-400 mb-6">
                  Anything that flared up since we last saw you?
                </p>
                <form.Field name="activeInjuries">
                  {(field) => (
                    <textarea
                      value={field.state.value.join(', ')}
                      onChange={(e) =>
                        field.handleChange(
                          e.target.value
                            .split(',')
                            .map((s) => s.trim())
                            .filter(Boolean)
                        )
                      }
                      placeholder="e.g. tight left hip, tender wrist"
                      className="w-full min-h-24 rounded-xl border border-cream-200 bg-white px-4 py-3 text-sm text-warm-gray-800 placeholder:text-warm-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-terracotta-400 mb-8"
                    />
                  )}
                </form.Field>
                <Button size="lg" className="w-full" onClick={advance}>
                  Continue →
                </Button>
              </CardBody>
            </Card>
          </motion.div>
        )}

        {step === 'intention' && (
          <motion.div key="intention" {...slide}>
            <Card elevated>
              <CardBody>
                <CardTitle className="mb-1">What do you want from today?</CardTitle>
                <p className="text-sm text-warm-gray-400 mb-6">
                  Loose or specific — just go with what comes
                </p>
                <form.Field name="intention">
                  {(field) => (
                    <textarea
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder={'e.g. "I just want to move" or "open my hips and calm down"'}
                      className="w-full min-h-24 rounded-xl border border-cream-200 bg-white px-4 py-3 text-sm text-warm-gray-800 placeholder:text-warm-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-terracotta-400 mb-8"
                    />
                  )}
                </form.Field>
                {error && (
                  <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-600 mb-4">
                    {error}
                  </div>
                )}
                <Button
                  size="lg"
                  className="w-full"
                  loading={isGenerating}
                  onClick={() => {
                    setError(null)
                    void form.handleSubmit()
                  }}
                >
                  {isGenerating ? 'Building your flow…' : 'Build my flow →'}
                </Button>
              </CardBody>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
